/**
 * Coerces one resolved Signify value into the value a Products-entity field
 * stores. Consumer: `useCreateLineItemFromSignify`, which calls it once per
 * destination it writes. `undefined` means "write nothing".
 *
 * Text destinations take an object's `formattedValue` / `displayValue` / `label`;
 * date destinations parse a date out of the string and store the `Timestamp`
 * that destination's type calls for — a `date` gets the calendar day, a
 * `dateTime` the instant, which is why `fieldDataTypeId` is passed through;
 * every other destination stores the raw value. A `SignifyPrice` object arrives
 * intact — the engine resolves `price.prices.<ID>` to the whole object — so it
 * is unwrapped first: `formattedValue` for a text destination, `value`
 * otherwise, and nothing at all when `value` is absent, which is the guard the
 * former price loop applied before writing.
 */
import { timestampFromDateString } from '@/dates';

const TEXT_FIELD_TYPES = ['text', 'shortText', 'longText'];
const DATE_FIELD_TYPES = ['date', 'dateTime'];
const DATE_PATTERNS = [
  /(\d{1,2}\/\d{1,2}\/\d{4})/,
  /(\d{4}-\d{2}-\d{2})/,
  /(\d{1,2}-\d{1,2}-\d{4})/,
];

type ObjectValue = Record<string, unknown>;

const isObject = (value: unknown): value is ObjectValue =>
  typeof value === 'object' && value !== null;

const isSignifyPrice = (value: unknown): value is { value: number | null; formattedValue: string } =>
  isObject(value) && 'priceTypeIdentifier' in value && 'value' in value;

const objectText = (value: ObjectValue): unknown =>
  value.formattedValue || value.displayValue || value.label || String(value);

const dateString = (value: unknown): unknown => {
  if (!isObject(value)) return value;
  const formattedValue = value.formattedValue || value.displayValue || value.label;
  if (typeof formattedValue !== 'string') return value;
  const match = DATE_PATTERNS.map((pattern) => formattedValue.match(pattern)).find((m) => m);
  return match ? match[1] : value;
};

export const coerceSignifyFieldValue = (params: {
  value: unknown;
  fieldDataTypeId: string | undefined;
}): unknown => {
  const { value, fieldDataTypeId } = params;
  const isTextField = fieldDataTypeId !== undefined && TEXT_FIELD_TYPES.includes(fieldDataTypeId);

  if (isSignifyPrice(value)) {
    if (value.value === null || value.value === undefined) return undefined;
    return isTextField && value.formattedValue ? value.formattedValue : value.value;
  }

  if (isTextField) {
    return isObject(value) ? objectText(value) : String(value);
  }

  if (fieldDataTypeId !== undefined && DATE_FIELD_TYPES.includes(fieldDataTypeId)) {
    const parsed = dateString(value);
    if (typeof parsed === 'string') {
      const timestamp = timestampFromDateString(parsed, fieldDataTypeId);
      if (timestamp) return timestamp;
    }
  }

  return value;
};
